/**
 * Chip di una singola etichetta email (colore da dato, non da palette fissa
 * in questo componente: la palette in etichetteColori.ts serve solo al
 * picker di creazione/modifica). Usata sia nella riga compatta della lista
 * (prop `compatta`) sia in testata al viewer (chip piena, con eventuale X
 * di rimozione).
 *
 * Se `etichetta` e' undefined (id orfano che non risolve a nessuna
 * etichetta nota) il chip non renderizza nulla: la risoluzione id -> dato
 * avviene nei consumer tramite una Map<string, EmailEtichetta>.
 */

import { X } from 'lucide-react';
import type { EmailEtichetta } from '../lib/api';

interface Props {
  etichetta: EmailEtichetta | undefined;
  /** Variante compatta per la riga della lista: solo pallino + nome troncato. */
  compatta?: boolean;
  /** Se presente mostra una X di rimozione (viewer, quando non soloLettura). */
  onRimuovi?: () => void;
  className?: string;
}

/** Converte un hex "#rrggbb" in rgba con alpha; fallback grigio se non valido. */
function hexToRgba(hex: string, alpha: number): string {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!match) return `rgba(148, 163, 184, ${alpha})`;
  const r = parseInt(match[1], 16);
  const g = parseInt(match[2], 16);
  const b = parseInt(match[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function EtichettaChip({ etichetta, compatta = false, onRimuovi, className }: Props) {
  if (!etichetta) return null;

  if (compatta) {
    return (
      <span
        className={`inline-flex max-w-[6rem] items-center gap-1 text-xs text-muted-foreground ${className ?? ''}`}
        title={etichetta.nome}
      >
        <span
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: etichetta.colore }}
          aria-hidden="true"
        />
        <span className="truncate">{etichetta.nome}</span>
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium text-muted-foreground ${className ?? ''}`}
      style={{ backgroundColor: hexToRgba(etichetta.colore, 0.15) }}
    >
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: etichetta.colore }}
        aria-hidden="true"
      />
      {etichetta.nome}
      {onRimuovi && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRimuovi();
          }}
          className="ml-0.5 rounded-full hover:opacity-70"
          aria-label={`Rimuovi etichetta ${etichetta.nome}`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}
